import { useState, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CalendarIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  TruckIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useSales } from '../hooks/useSales';
import {
  Sale,
  SaleStatus,
  PaymentMethod,
  formatCurrency,
  getSaleStatusColor,
  getPaymentMethodDisplay,
  salesApi,
} from '../api/sales';
import { safeNumber } from '../utils/formatters';
import CreateSaleModal from '../components/sales/CreateSaleModal';
import EditSaleModal from '../components/sales/EditSaleModal';
import ViewSaleModal from '../components/sales/ViewSaleModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';

interface SaleFilters {
  status?: SaleStatus;
  paymentMethod?: PaymentMethod;
  dateRange?: {
    start: string;
    end: string;
  };
  customer?: string;
  amountRange?: {
    min: number;
    max: number;
  };
}

export default function Sales() {
  const {
    sales,
    analytics,
    loading,
    error,
    createSale,
    updateSale,
    deleteSale,
    refreshData,
  } = useSales();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SaleFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof Sale>('saleDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);

  // Filter and search logic
  const filteredSales = useMemo(() => {
    let result = sales;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(sale =>
        sale.buyerName.toLowerCase().includes(term) ||
        sale.invoiceNumber.toLowerCase().includes(term) ||
        sale.items.some(item => item.productName.toLowerCase().includes(term))
      );
    }

    // Apply status filter
    if (filters.status) {
      result = result.filter(sale => sale.status === filters.status);
    }

    // Apply payment method filter
    if (filters.paymentMethod) {
      result = result.filter(sale => sale.paymentMethod === filters.paymentMethod);
    }

    // Apply date range filter
    if (filters.dateRange?.start && filters.dateRange?.end) {
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      result = result.filter(sale => {
        const saleDate = new Date(sale.saleDate);
        return saleDate >= startDate && saleDate <= endDate;
      });
    }

    // Apply customer filter
    if (filters.customer) {
      const customerTerm = filters.customer.toLowerCase();
      result = result.filter(sale =>
        sale.buyerName.toLowerCase().includes(customerTerm)
      );
    }

    // Apply amount range filter
    if (filters.amountRange?.min !== undefined || filters.amountRange?.max !== undefined) {
      result = result.filter(sale => {
        const { min, max } = filters.amountRange!;
        if (min !== undefined && sale.total < min) return false;
        if (max !== undefined && sale.total > max) return false;
        return true;
      });
    }

    return result;
  }, [sales, searchTerm, filters]);

  // Sort logic
  const sortedSales = useMemo(() => {
    return [...filteredSales].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle date sorting
      if (sortBy === 'saleDate' || sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [filteredSales, sortBy, sortOrder]);

  // Pagination logic
  const paginatedSales = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedSales.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedSales, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedSales.length / itemsPerPage);

  // Modal handlers
  const handleCreateSale = async (data: any) => {
    const result = await createSale(data);
    if (result) {
      setShowCreateModal(false);
    }
  };

  const handleUpdateSale = async (data: any) => {
    if (selectedSale) {
      const result = await updateSale(selectedSale.id, data);
      if (result) {
        setShowEditModal(false);
        setSelectedSale(null);
      }
    }
  };

  const handleDeleteSale = async () => {
    if (selectedSale) {
      const result = await deleteSale(selectedSale.id);
      if (result) {
      setShowDeleteModal(false);
      setSelectedSale(null);
      }
    }
  };

  const handleStatusChange = async (saleId: string, status: SaleStatus) => {
    try {
      await salesApi.updateSaleStatus(saleId, status);
      await refreshData(); // Refresh the sales list to show updated status
      
      // Show success message
      const statusMessages = {
        [SaleStatus.PENDING]: 'Sale status changed to Pending',
        [SaleStatus.COMPLETED]: 'Sale marked as Complete',
        [SaleStatus.CANCELLED]: 'Sale has been Cancelled',
        [SaleStatus.REFUNDED]: 'Sale has been Refunded',
      };
      toast.success(statusMessages[status] || 'Sale status updated');
    } catch (error) {
      console.error('Error updating sale status:', error);
      toast.error('Failed to update sale status');
      throw error; // Re-throw to allow EditSaleModal to handle the error
    }
  };

  const openEditModal = (sale: Sale) => {
    setSelectedSale(sale);
    setShowEditModal(true);
  };

  const openViewModal = (sale: Sale) => {
    setSelectedSale(sale);
    setShowViewModal(true);
  };

  const openDeleteModal = (sale: Sale) => {
    setSelectedSale(sale);
    setShowDeleteModal(true);
  };

  const getStatusIcon = (status: SaleStatus) => {
    switch (status) {
      case SaleStatus.PENDING:
        return <ClockIcon className="h-4 w-4" />;
      case SaleStatus.COMPLETED:
        return <CheckCircleIcon className="h-4 w-4" />;
      case SaleStatus.CANCELLED:
        return <XCircleIcon className="h-4 w-4" />;
      case SaleStatus.REFUNDED:
        return <TruckIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  if (loading && sales.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading sales</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
            <button
                onClick={refreshData}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
            >
                Try again
            </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHero
        title="Sales"
        subtitle="Track bird sales, invoices, and customer transactions"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            New Sale
          </button>
        }
      />

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Revenue</dt>
                  <dd className="text-lg font-medium text-slate-900">
                      {formatCurrency(safeNumber(analytics.totalSalesAmount))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                  <DocumentDuplicateIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Sales</dt>
                    <dd className="text-lg font-medium text-slate-900">{safeNumber(analytics.totalSalesCount)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-slate-900">
                      {safeNumber(analytics.salesCountByStatus.COMPLETED)}
                    </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-slate-900">
                      {safeNumber(analytics.salesCountByStatus.PENDING)}
                    </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
          <div className="flex items-center w-full rounded-md border border-gray-300 bg-white focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <input
                type="text"
                className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent leading-5 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400 sm:text-sm"
                placeholder="Search sales by customer, invoice, or item..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
          </div>
        </div>

          <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>

            <div className="flex rounded-md shadow-sm">
            <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 text-sm font-medium border rounded-l-md ${
                viewMode === 'grid'
                    ? 'bg-cyan-50 border-cyan-500 text-cyan-600'
                    : 'bg-white border-gray-300 text-slate-700 hover:bg-slate-50/80'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 text-sm font-medium border-t border-r border-b rounded-r-md ${
                viewMode === 'table'
                    ? 'bg-cyan-50 border-cyan-500 text-cyan-600'
                    : 'bg-white border-gray-300 text-slate-700 hover:bg-slate-50/80'
              }`}
            >
              <TableCellsIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
                <label htmlFor="status-filter" className="block text-sm font-medium text-slate-700">
                  Status
              </label>
              <select
                  id="status-filter"
                value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as SaleStatus || undefined }))}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                <option key="empty-option-1753461297396" value="">All Statuses</option>
                  {Object.values(SaleStatus).map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                    </option>
                  ))}
              </select>
            </div>

            <div>
                <label htmlFor="payment-filter" className="block text-sm font-medium text-slate-700">
                  Payment Method
              </label>
              <select
                  id="payment-filter"
                  value={filters.paymentMethod || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod || undefined }))}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="empty-option-1753461297396" value="">All Methods</option>
                  {Object.values(PaymentMethod).map(method => (
                    <option key={method} value={method}>
                      {getPaymentMethodDisplay(method)}
                    </option>
                  ))}
              </select>
            </div>

            <div>
                <label htmlFor="sort-by" className="block text-sm font-medium text-slate-700">
                  Sort By
              </label>
              <select
                  id="sort-by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as keyof Sale)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="saleDate-1753461297396" value="saleDate">Sale Date</option>
                  <option key="buyerName-1753461297396" value="buyerName">Customer</option>
                  <option key="total-1753461297396" value="total">Amount</option>
                  <option key="status-1753461297396" value="status">Status</option>
              </select>
            </div>

            <div>
                <label htmlFor="sort-order" className="block text-sm font-medium text-slate-700">
                  Order
              </label>
              <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="desc-1753461297396" value="desc">Newest First</option>
                  <option key="asc-1753461297396" value="asc">Oldest First</option>
              </select>
          </div>

            <div>
                <label htmlFor="customer-filter" className="block text-sm font-medium text-slate-700">
                Customer
              </label>
              <input
                type="text"
                  id="customer-filter"
                value={filters.customer || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, customer: e.target.value || undefined }))}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  placeholder="Filter by customer..."
              />
            </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setFilters({})}
                className="text-sm text-cyan-600 hover:text-cyan-500"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}
        </div>

      {/* Sales List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-slate-900">
            Sales ({sortedSales.length})
          </h3>
        </div>

        {paginatedSales.length === 0 ? (
          <EmptyState
            className="mt-0 rounded-none border-0 shadow-none bg-transparent"
            title="No sales found"
            description={
              sales.length === 0
                ? 'Get started by creating your first sale.'
                : 'Try adjusting your search criteria.'
            }
            icon={<BanknotesIcon className="h-6 w-6" />}
            action={
              sales.length === 0 ? (
                <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary text-sm focus-pop">
                  <PlusIcon className="h-4 w-4" />
                  New Sale
                </button>
              ) : undefined
            }
          />
        ) : viewMode === 'grid' ? (
          // Grid View
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedSales.map((sale) => (
            <div
              key={sale.id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openViewModal(sale)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-cyan-600">
                          {sale.invoiceNumber}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSaleStatusColor(sale.status)}`}>
                          {getStatusIcon(sale.status)}
                          <span className="ml-1">{sale.status}</span>
                        </span>
                </div>
                      
                      <h3 className="text-lg font-medium text-slate-900 truncate">
                        {sale.buyerName}
                </h3>
                      
                      <p className="text-sm text-slate-500 mb-2">
                        {format(new Date(sale.saleDate), 'MMM dd, yyyy')}
                      </p>
                      
                      <div className="text-xl font-bold text-slate-900 mb-2">
                        {formatCurrency(sale.total)}
                    </div>
                      
                      <p className="text-sm text-slate-500">
                        {getPaymentMethodDisplay(sale.paymentMethod)}
                      </p>
                      
                      <p className="text-sm text-slate-500 mt-1">
                        {sale.items.length} item{sale.items.length !== 1 ? 's' : ''}
                      </p>
              </div>

                    <div className="flex-shrink-0 ml-4">
                      <div className="flex space-x-2">
                    <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(sale);
                          }}
                          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-gray-100 rounded-full"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(sale);
                          }}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-gray-100 rounded-full"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
            </div>
        </div>
      ) : (
          // Table View
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-slate-50/80">
                    <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Invoice
                      </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Customer
                      </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date
                      </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Amount
                      </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Payment
                      </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                      </th>
                    </tr>
                  </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSales.map((sale) => (
                  <tr
                    key={sale.id}
                    className="hover:bg-slate-50/80 cursor-pointer"
                    onClick={() => openViewModal(sale)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-cyan-600">
                      {sale.invoiceNumber}
                        </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {sale.buyerName}
                          </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {format(new Date(sale.saleDate), 'MMM dd, yyyy')}
                          </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {formatCurrency(sale.total)}
                          </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSaleStatusColor(sale.status)}`}>
                        {sale.status}
                      </span>
                          </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {getPaymentMethodDisplay(sale.paymentMethod)}
                          </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                              <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openViewModal(sale);
                          }}
                                className="text-cyan-600 hover:text-indigo-900"
                                title="View"
                              >
                          <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(sale);
                          }}
                                className="text-slate-600 hover:text-slate-900"
                                title="Edit"
                              >
                          <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(sale);
                          }}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                          <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                ))}
                  </tbody>
                </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, sortedSales.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{sortedSales.length}</span> results
              </p>
            </div>
            <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-cyan-50 border-cyan-500 text-cyan-600'
                          : 'bg-white border-gray-300 text-slate-500 hover:bg-slate-50/80'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
                </div>

      {/* Modals */}
      <CreateSaleModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSale}
      />

      <EditSaleModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSale(null);
        }}
        onSubmit={handleUpdateSale}
        onStatusChange={handleStatusChange}
        sale={selectedSale}
      />

      <ViewSaleModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedSale(null);
        }}
        sale={selectedSale}
        onEdit={() => {
          setShowViewModal(false);
          setShowEditModal(true);
        }}
        onDelete={() => {
          setShowViewModal(false);
          setShowDeleteModal(true);
        }}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSale(null);
        }}
        onConfirm={handleDeleteSale}
        title="Delete Sale"
        message={`Are you sure you want to delete sale "${selectedSale?.invoiceNumber}"? This action cannot be undone.`}
      />
    </div>
  );
} 